import React from 'react';
import './FeaturedBlock.scss';
import { useFeaturedItems } from './useFeaturedItems';
import type { FeaturedItemData } from './FeaturedService';
import { environment } from 'nsdi-volto-frontend/config/environments';

interface FeaturedBlockProps {
  data?: {
    sectionTitle?: string;
    items?: FeaturedItemData[];
    columns?: number;
    showFormat?: boolean;
    showShowAll?: boolean;
    maxItemsToShow?: number;
    // Card display options
    cardDisplayFields?: CardDisplayFieldConfig[] | string;
    cardBadgeField?: string;
    // Dynamic fetching options
    fetchDynamic?: boolean;
    voyagerBaseUrl?: string;
    voyagerConfigId?: string;
    featuredQuery?: string;
    // Search results page URL
    searchResultsBaseUrl?: string;
    // Details page base URL
    detailsPageBaseUrl?: string;
  };
}

// Type for card display field configuration
interface CardDisplayFieldConfig {
  field: string;
  label: string;
}

// Parse cardDisplayFields - handles both array format (new) and JSON string (legacy)
const parseCardDisplayFields = (
  input?: CardDisplayFieldConfig[] | string,
): CardDisplayFieldConfig[] => {
  const defaultFields: CardDisplayFieldConfig[] = [
    { field: 'format_category', label: 'Format' },
    { field: 'description', label: 'Description' },
  ];

  if (!input) {
    return defaultFields;
  }

  // If it's already an array, use it directly
  if (Array.isArray(input)) {
    return input.filter(
      (f) => f && typeof f.field === 'string' && typeof f.label === 'string',
    );
  }

  // If it's a string, try to parse as JSON (legacy format)
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) {
        return parsed.filter((f) => f && typeof f.field === 'string');
      }
    } catch {
      // Ignore parse errors
    }
  }

  return defaultFields;
};

// Get field value from item (handles various field mappings)
const getFieldValue = (
  item: FeaturedItemData & Record<string, unknown>,
  field: string,
): string | undefined => {
  // Map common field names to item properties
  const fieldMappings: Record<string, keyof FeaturedItemData | string> = {
    format_category: 'format',
    format_type: 'formatType',
    format_app: 'formatApp',
    format: 'format',
    description: 'description',
    title: 'title',
    views: 'views',
    fileSize: 'fileSize',
    file_size: 'fileSize',
    bytes: 'fileSize',
    platform: 'platform',
    event: 'event',
    tags: 'tags',
    keywords: 'keywords',
  };

  const mappedField = fieldMappings[field] || field;
  const value = item[mappedField as keyof FeaturedItemData];

  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
};

// Determine badge indicator color based on badge text content
const getBadgeIndicatorColor = (badgeText: string): string => {
  const text = badgeText.toLowerCase();

  // Orange/warning indicators
  if (
    text.includes('fire') ||
    text.includes('damage') ||
    text.includes('detected') ||
    text.includes('warning') ||
    text.includes('alert') ||
    text.includes('hazard')
  ) {
    return 'orange';
  }

  // Red/critical indicators
  if (
    text.includes('critical') ||
    text.includes('severe') ||
    text.includes('emergency') ||
    text.includes('destroyed') ||
    text.includes('high risk')
  ) {
    return 'red';
  }

  // Blue indicators
  if (
    text.includes('water') ||
    text.includes('flood') ||
    text.includes('storm') ||
    text.includes('track') ||
    text.includes('hurricane') ||
    text.includes('idalia')
  ) {
    return 'blue';
  }

  // Green/positive indicators (default)
  if (
    text.includes('undamaged') ||
    text.includes('safe') ||
    text.includes('clear') ||
    text.includes('detection') ||
    text.includes('monitoring')
  ) {
    return 'green';
  }

  // Default to green
  return 'green';
};

// Placeholder thumbnail for items without images
const PLACEHOLDER_THUMBNAIL =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="120" viewBox="0 0 200 120"%3E%3Crect fill="%23e0e0e0" width="200" height="120"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';

const FeaturedBlock: React.FC<FeaturedBlockProps> = ({ data }) => {
  const sectionTitle = data?.sectionTitle || 'Featured';
  const columns = data?.columns || 6;
  const showShowAll = data?.showShowAll !== false;
  const maxItemsToShow = data?.maxItemsToShow || 12;
  const fetchDynamic = data?.fetchDynamic !== false; // Default to true for dynamic fetching
  const searchResultsBaseUrl =
    data?.searchResultsBaseUrl || environment.voyager.searchUrl;
  const featuredQuery = data?.featuredQuery || 'tag_tags:featured';
  const detailsPageBaseUrl =
    data?.detailsPageBaseUrl || environment.voyager.detailsUrl;

  // Card display configuration
  const cardDisplayFields = parseCardDisplayFields(data?.cardDisplayFields);
  const cardBadgeField = data?.cardBadgeField || 'format_type';

  // Use the hook to fetch featured items
  const {
    items: fetchedItems,
    isLoading,
    error,
  } = useFeaturedItems({
    fetchDynamic,
    voyagerBaseUrl: data?.voyagerBaseUrl,
    voyagerConfigId: data?.voyagerConfigId,
    featuredQuery: featuredQuery,
    maxItems: maxItemsToShow,
    staticItems: data?.items || [],
    detailsPageBaseUrl: detailsPageBaseUrl,
  });

  // Limit displayed items
  const items = fetchedItems.slice(0, maxItemsToShow);

  const handleShowAll = () => {
    // Navigate to full search results for featured items using configured query
    // If query is a simple filter (no =), wrap in fq=
    // If query contains =, treat it as full query params
    if (featuredQuery.includes('=')) {
      // Full query string - append directly
      const separator = searchResultsBaseUrl.includes('?') ? '&' : '?';
      window.location.href = `${searchResultsBaseUrl}${separator}${featuredQuery}`;
    } else {
      // Simple filter - use fq parameter
      window.location.href = `${searchResultsBaseUrl}?fq=${encodeURIComponent(featuredQuery)}`;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <section
        className="featured-block featured-block--loading"
        aria-labelledby="featured-title"
      >
        <h2 id="featured-title" className="featured-block__title">
          {sectionTitle}
        </h2>
        <div className="featured-block__loading">
          <span className="featured-block__spinner" aria-hidden="true" />
          <span>Loading featured items...</span>
        </div>
      </section>
    );
  }

  // Error state (show message but still render any available items)
  const showError = error && items.length === 0;

  return (
    <section className="featured-block" aria-labelledby="featured-title">
      <h2 id="featured-title" className="featured-block__title">
        {sectionTitle}
      </h2>

      {showError && (
        <div className="featured-block__error" role="alert">
          <p>Unable to load featured items. Please try again later.</p>
        </div>
      )}

      {items.length > 0 && (
        <div
          className={`featured-block__grid featured-block__grid--cols-${columns}`}
          role="list"
        >
          {items.map((item) => {
            // Get badge value from configured field
            const badgeValue = cardBadgeField
              ? getFieldValue(
                  item as FeaturedItemData & Record<string, unknown>,
                  cardBadgeField,
                )
              : undefined;

            return (
              <article key={item.id} className="featured-card" role="listitem">
                {/* Thumbnail */}
                <a href={item.link || '#'} className="featured-card__thumbnail">
                  {badgeValue && (
                    <div className="featured-card__badge">
                      <span
                        className={`featured-card__badge-indicator featured-card__badge-indicator--${getBadgeIndicatorColor(badgeValue)}`}
                      />
                      <span className="featured-card__badge-text">
                        {badgeValue}
                      </span>
                    </div>
                  )}
                  <img
                    src={item.thumbnail || PLACEHOLDER_THUMBNAIL}
                    alt={`Thumbnail for ${item.title}`}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.src !== PLACEHOLDER_THUMBNAIL) {
                        target.src = PLACEHOLDER_THUMBNAIL;
                      }
                    }}
                  />
                </a>

                <hr className="featured-card__divider" />

                {/* Content */}
                <div className="featured-card__content">
                  {item.title && (
                    <h3 className="featured-card__title">
                      <a
                        href={item.link || '#'}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.title}
                      </a>
                    </h3>
                  )}

                  {/* Metadata - rendered from cardDisplayFields configuration */}
                  <div className="featured-card__meta">
                    {cardDisplayFields.map((fieldConfig, idx) => {
                      const value = getFieldValue(
                        item as FeaturedItemData & Record<string, unknown>,
                        fieldConfig.field,
                      );
                      if (!value) return null;
                      return (
                        <p key={idx} className="featured-card__meta-item">
                          <span className="featured-card__meta-label">
                            {fieldConfig.label}:
                          </span>{' '}
                          <span className="featured-card__meta-value">
                            {value}
                          </span>
                        </p>
                      );
                    })}
                  </div>

                  <hr className="featured-card__divider" />

                  {/* Action buttons */}
                  <div className="featured-card__actions-row">
                    <button
                      className="featured-card__btn featured-card__btn--dropdown"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Tools <span className="featured-card__btn-arrow">▾</span>
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showShowAll && items.length > 0 && (
        <div className="featured-block__footer">
          <button className="featured-block__show-all" onClick={handleShowAll}>
            Show All
          </button>
        </div>
      )}
    </section>
  );
};

export default FeaturedBlock;
